import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, getArticlesByCategory } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'xinlixue-jiexi');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryArticles = getArticlesByCategory(activeCategory);

  const categoryTitle = currentCategory ? `${currentCategory.name} - 明智育儿指南` : '分类页面';
  const categoryDesc = currentCategory
    ? `${currentCategory.description}。浏览${currentCategory.name}相关文章，获取专业的育儿指导和管教建议。`
    : '';

  return (
    <>
      <SEO
        title={categoryTitle}
        description={categoryDesc}
        keywords={`打屁股,${currentCategory?.name || ''},儿童行为管理,家庭教育,正面管教,育儿知识`}
        canonicalUrl={`https://aitdev.cn/category/${activeCategory}`}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory?.name || '分类' },
              ]}
            />
            <div className="pb-10">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                {currentCategory?.name}
              </h1>
              <p className="text-sm text-foreground-600 max-w-2xl leading-relaxed">
                {currentCategory?.description}
              </p>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="border-b border-background-200/70 bg-background-50 sticky top-16 z-40">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-2 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Articles List */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-file-list-3-line text-2xl"></i>
                </div>
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}